import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaBars, FaGlobe, FaMobileAlt, FaSignOutAlt, FaQuestionCircle, FaUserCircle, FaHome, FaTimes, FaInfoCircle, FaCopy } from 'react-icons/fa';
import { Chart } from 'react-google-charts';
import axios from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.png';
import airplaneImage from '../../assets/airplane.png';
import '../Home/Home.css';
import './Insights.css';
import { initialFilterPool, filterPoolForCategory, buildTxQuery, buildSubQuery, txSeriesChartData, subSeriesChartData, defaultMetricForCategory } from './InsightsUtils';

const Insights = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const toggleHelp = () => setShowHelp(s => !s);

  // Filters state
  const [filterValues, setFilterValues] = useState({ currencies: [], transaction_statuses: [], lounges: [], users: [], subscription_statuses: [], plan_tiers: [] });
  const [category, setCategory] = useState('transactions');
  const [activeFilters, setActiveFilters] = useState([]);
  const [availableFilters, setAvailableFilters] = useState([]);
  const [filtersState, setFiltersState] = useState({
    date_preset: 'this_year',
    granularity: 'day',
    currency: '',
    status: '',
    lounge_id: '',
    user_id: '',
    subscription_status: '',
    plan_tier: '',
    metric: 'transaction_amount'
  });

  // Data
  const [txData, setTxData] = useState(null);
  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Drag
  const dragItem = useRef(null);

  // FK related
  const [openDropdown, setOpenDropdown] = useState(null); // key
  const [fkPopup, setFkPopup] = useState(null); // {key,id,loading,data}
  const loungeCache = useRef({});
  const userCache = useRef({});
  const loungeDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  const token = localStorage.getItem('firebaseToken');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // Load filter lists and hydrate lounge IDs with names
  useEffect(()=>{
    axios.get('/api/insights/filters').then(async r => {
      const data = r.data;
      // If lounges is array of IDs, fetch lounge details for each
      if (Array.isArray(data.lounges) && data.lounges.length > 0 && typeof data.lounges[0] !== 'object') {
        try {
          // Fetch all lounges in parallel
          const loungeDetails = await Promise.all(
            data.lounges.map(async id => {
              try {
                const loungeRes = await axios.get(`/lounges/${id}`, { headers: authHeaders });
                const lounge = loungeRes.data;
                let IATACode = '';
                if (lounge.AirportID) {
                  try {
                    const airportRes = await axios.get(`/airport/${lounge.AirportID}`, { headers: authHeaders });
                    IATACode = airportRes.data.IATACode || '';
                  } catch {}
                }
                return { id, LoungeName: lounge.LoungeName || String(id), IATACode };
              } catch {
                return { id, LoungeName: String(id), IATACode: '' };
              }
            })
          );
          data.lounges = loungeDetails;
        } catch (e) {
          // fallback: just use IDs
        }
      }
      setFilterValues(data);
    }).catch(()=>{});
  },[]);

  // Adjust filters on category change
  useEffect(()=>{ setAvailableFilters(filterPoolForCategory(category)); setActiveFilters([]); setFiltersState(p=>({...p, metric: defaultMetricForCategory(category)})); },[category]);

  const updateFilterValue = (k,v)=> setFiltersState(p=>({...p,[k]:v}));
  const addFilter = f => { setActiveFilters(p=> p.find(x=>x.key===f.key)?p:[...p,f]); setAvailableFilters(p=> p.filter(x=>x.key!==f.key)); };
  const removeFilter = key => {
    setActiveFilters(p=> p.filter(f=>f.key!==key));
    const meta=initialFilterPool.find(m=>m.key===key);
    if(meta) setAvailableFilters(p=>[...p,meta]);
    // Map of default reset values
    const defaultReset = {
      date_preset: 'this_year',
      granularity: 'day',
      currency: '',
      status: '',
      lounge_id: '',
      user_id: '',
      subscription_status: '',
      plan_tier: '',
      metric: filtersState.metric // do not change metric when removing chip; keep current selection
    };
    if(Object.prototype.hasOwnProperty.call(defaultReset, key)) {
      setFiltersState(prev => ({ ...prev, [key]: defaultReset[key] }));
    }
    if(key==='lounge_id' || key==='user_id') {
      if(openDropdown===key) setOpenDropdown(null);
    }
  }

  // Drag handlers
  const onDragStart = (e,f,origin)=>{ dragItem.current={f,origin}; e.dataTransfer.effectAllowed='move'; };
  const onDragOver = e=> e.preventDefault();
  const onDropActive = e=>{ e.preventDefault(); if(!dragItem.current) return; const {f,origin}=dragItem.current; if(origin==='available') addFilter(f); };
  const onDropAvailable = e=>{ e.preventDefault(); if(!dragItem.current) return; const {f,origin}=dragItem.current; if(origin==='active') removeFilter(f.key); };

  // Data fetch
  const fetchData = useCallback(()=>{
    setLoading(true);
    const jobs=[];
    if(category==='transactions' && ['transaction_amount','transaction_count'].includes(filtersState.metric)){
      jobs.push(axios.get(buildTxQuery(filtersState)).then(r=>({t:'tx',d:r.data})).catch(e=>({t:'txErr',e})));
    }
    if(category==='subscriptions' && filtersState.metric==='subscription_count'){
      jobs.push(axios.get(buildSubQuery(filtersState)).then(r=>({t:'sub',d:r.data})).catch(e=>({t:'subErr',e})));
    }
    Promise.all(jobs).then(resArr=>{
  resArr.forEach(r=>{ if(r.t==='tx') setTxData(r.d); else if(r.t==='sub') setSubData(r.d); else if(r.t==='txErr' || r.t==='subErr') console.error(r.e); });
    }).finally(()=> setLoading(false));
  },[filtersState,category]);
  useEffect(()=>{ fetchData(); },[fetchData]);

  // FK fetchers
  async function fetchLoungeDetails(id){ if(!id) return null; if(loungeCache.current[id]) return loungeCache.current[id]; try{ const r=await axios.get(`/lounges/${id}`,{headers:authHeaders}); loungeCache.current[id]=r.data; return r.data; }catch(e){ try{ const list=await axios.get('/lounges',{headers:authHeaders}); const found=(list.data||[]).find(l=> String(l.LoungeID)===String(id) || String(l.LoungeName).toLowerCase()===String(id).toLowerCase()); if(found){ loungeCache.current[id]=found; return found;} return {__error:404,__message:'Not found'}; }catch(err){ return {__error:'err',__message:'Fetch error'}; } } }
  async function fetchUserDetails(id){ if(!id) return null; if(userCache.current[id]) return userCache.current[id]; try{ const r=await axios.get(`/profiles/${id}`,{headers:authHeaders}); userCache.current[id]=r.data; return r.data; }catch(e){ try{ const list=await axios.get('/profiles',{headers:authHeaders}); const found=(list.data||[]).find(u=> String(u.ID)===String(id) || String(u.Email).toLowerCase()===String(id).toLowerCase()); if(found){ userCache.current[id]=found; return found;} return {__error:404,__message:'Not found'}; }catch(err){ return {__error:'err',__message:'Fetch error'}; } } }
  const openFkPopup = (key,id,fetcher)=>{ setFkPopup({key,id,loading:true,data:null}); fetcher(id).then(data=> setFkPopup({key,id,loading:false,data})); };
  const closeFkPopup = ()=> setFkPopup(null);

  const handleCopy = (text) => {
    if(!text) return;
    try { navigator.clipboard.writeText(String(text)); } catch(e) { console.warn('Copy failed', e); }
  };

  // Outside click
  useEffect(()=>{ function outside(e){ if(!openDropdown) return; const ref = openDropdown==='lounge_id'? loungeDropdownRef.current : openDropdown==='user_id'? userDropdownRef.current : null; if(ref && !ref.contains(e.target)) setOpenDropdown(null);} document.addEventListener('mousedown',outside); return ()=> document.removeEventListener('mousedown',outside); },[openDropdown]);

  const toggleDropdown = key => setOpenDropdown(p=> p===key? null : key);


  // Lounge dropdown: show id and name
  const FKDropdown = ({dropdownKey, options, value, onChange, onInfo, dropdownRef}) => {
    const isLounge = dropdownKey === 'lounge_id';
    return (
      <div className='fk-dropdown' ref={dropdownRef}>
        <div
          className='insights-filter-value-select fk-trigger-select'
          role='button'
          tabIndex={0}
          aria-haspopup='listbox'
          aria-expanded={openDropdown===dropdownKey}
          onClick={()=>toggleDropdown(dropdownKey)}
          onKeyDown={e=>{/* ...existing code... */}}
        >
          {/* Show selected lounge as id - name, or just value */}
          {isLounge
            ? (options.find(opt => String(opt.id) === String(value)) ? `${options.find(opt => String(opt.id) === String(value)).id} - ${options.find(opt => String(opt.id) === String(value)).LoungeName}` : 'All')
            : (value || 'All')}
        </div>
        {openDropdown===dropdownKey && (
          <div className='fk-menu' role='listbox'>
            <div className={`fk-menu-item ${!value?'active':''}`} role='option' aria-selected={!value} onClick={()=>{ onChange(''); setOpenDropdown(null); }}>All</div>
            {options.map(opt => {
              // For lounge, opt is {id, LoungeName}, else string
              const optValue = isLounge ? String(opt.id) : opt;
              const optLabel = isLounge ? `${opt.id} - ${opt.LoungeName}${opt.IATACode ? ' - ' + opt.IATACode : ''}` : opt;
              return (
                <div key={optValue} className={`fk-menu-item ${String(value)===optValue?'active':''}`} role='option' aria-selected={String(value)===optValue} onClick={()=>{ isLounge ? onChange(opt.id) : onChange(opt); setOpenDropdown(null); }}>
                  <span style={{flex:1}}>{optLabel}</span>
                  <button type='button' className='fk-info-btn' onClick={e=>{ e.stopPropagation(); isLounge ? onInfo(opt.id) : onInfo(opt); }} aria-label={`Info for ${optLabel}`}><FaInfoCircle/></button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderValueControl = key => {
    switch(key){
      case 'date_preset': return (<select value={filtersState.date_preset} onChange={e=>updateFilterValue('date_preset',e.target.value)} className='insights-filter-value-select'><option value='today'>Today</option><option value='this_week'>This Week</option><option value='this_month'>This Month</option><option value='this_year'>This Year</option></select>);
      case 'granularity': return (<select value={filtersState.granularity} onChange={e=>updateFilterValue('granularity',e.target.value)} className='insights-filter-value-select'><option value='day'>Day</option><option value='week'>Week</option><option value='month'>Month</option></select>);
      case 'currency': return (<select value={filtersState.currency} onChange={e=>updateFilterValue('currency',e.target.value)} className='insights-filter-value-select'><option value=''>All</option>{filterValues.currencies.map(c=> <option key={c} value={c}>{c}</option>)}</select>);
      case 'status': return (<select value={filtersState.status} onChange={e=>updateFilterValue('status',e.target.value)} className='insights-filter-value-select'><option value=''>All</option>{filterValues.transaction_statuses.map(s=> <option key={s} value={s}>{s}</option>)}</select>);
      case 'lounge_id': return (<FKDropdown dropdownKey='lounge_id' options={filterValues.lounges} value={filtersState.lounge_id} onChange={v=>updateFilterValue('lounge_id',v)} onInfo={id=>openFkPopup('lounge_id',id,fetchLoungeDetails)} dropdownRef={loungeDropdownRef}/>);
      case 'user_id': return (<FKDropdown dropdownKey='user_id' options={filterValues.users} value={filtersState.user_id} onChange={v=>updateFilterValue('user_id',v)} onInfo={id=>openFkPopup('user_id',id,fetchUserDetails)} dropdownRef={userDropdownRef}/>);
      case 'subscription_status': return (<select value={filtersState.subscription_status} onChange={e=>updateFilterValue('subscription_status',e.target.value)} className='insights-filter-value-select'><option value=''>All</option>{filterValues.subscription_statuses.map(s=> <option key={s} value={s}>{s}</option>)}</select>);
      case 'plan_tier': return (
        <select value={filtersState.plan_tier} onChange={e=>updateFilterValue('plan_tier',e.target.value)} className='insights-filter-value-select'>
          <option value=''>All</option>
          {filterValues.plan_tiers.map(pt => <option key={pt} value={pt}>{pt}</option>)}
        </select>
      );
      case 'metric': return (<select value={filtersState.metric} onChange={e=>updateFilterValue('metric',e.target.value)} className='insights-filter-value-select'>{category==='transactions' && <><option value='transaction_amount'>Transaction Amount</option><option value='transaction_count'>Transaction Count</option></>}{category==='subscriptions' && <option value='subscription_count'>Subscription Count</option>}</select>);
      default: return null;
    }
  };

  const txChartData = txSeriesChartData(txData, filtersState.metric);
  const googlePieData = [[filtersState.metric==='transaction_amount'?'Bucket (Amount)':'Bucket (Count)', filtersState.metric==='transaction_amount'?'Amount':'Count'], ...txChartData.map(d=>[d.name,d.value])];
  const pieOptions = { legend:{position:'right', textStyle:{fontSize:10}}, chartArea:{width:'70%', height:'80%'}, fontName:'Inter, Arial, sans-serif', colors:["#1572dd", "#0e265a", "#f7b731", "#349922", "#20bf6b", "#8854d0", "#fd9644", "#4b6584"], sliceVisibilityThreshold:0 };

  // Dynamic metric/y-axis labeling for clarity
  const metricYAxisTitle = (() => {
    if(category==='transactions') {
      if(filtersState.metric==='transaction_amount') {
        return `Transaction Amount (${filtersState.currency || 'All Currencies'})`;
      }
      if(filtersState.metric==='transaction_count') return 'Transaction Count';
    }
    if(category==='subscriptions') return 'Subscriptions Count';
    return '';
  })();

  const summaryParts = (()=>{ const p=[]; const m=filtersState; const dm={today:'Today',this_week:'This Week',this_month:'This Month',this_year:'This Year'}; if(m.date_preset)p.push(`Date: ${dm[m.date_preset]||m.date_preset}`); if(m.granularity)p.push(`Granularity: ${m.granularity}`); if(m.currency)p.push(`Currency: ${m.currency}`); if(m.status)p.push(`Tx Status: ${m.status}`); if(m.lounge_id)p.push(`Lounge: ${m.lounge_id}`); if(m.user_id)p.push(`User: ${m.user_id}`); if(m.subscription_status)p.push(`Sub Status: ${m.subscription_status}`); if(m.plan_tier)p.push(`Plan Tier: ${m.plan_tier}`); return p; })();

  const handleLogout = async ()=>{ await logout(); navigate('/login'); };
  const openInNewTab = url => window.open(url,'_blank','noopener,noreferrer');

  return (
    <div className='layout-wrapper layout-wrapper-auto' style={{backgroundColor:'#f0f4ff'}}>
      <header className='top-bar' style={{position:'relative'}}>
        <div className='sidebar-top-row' style={{position:'absolute', left:20, top:'50%', transform:'translateY(-50%)', display:'flex', alignItems:'center'}}>
          {!collapsed && <img src={logo} alt='Logo' className='sidebar-logo' onClick={()=>navigate('/home')} />}
          <button className='menu-btn sidebar-menu-btn' onClick={()=> setCollapsed(c=>!c)} aria-label='Toggle sidebar'><FaBars size={24}/></button>
        </div>
        <h1 className='portal-title' style={{margin:0, position:'absolute', left:0, right:0, textAlign:'center', pointerEvents:'none'}}>Lounge Atlas Portal</h1>
        <div style={{position:'absolute', right:20, top:'50%', transform:'translateY(-50%)', display:'flex', gap:12}}>
          <button className='class1-btn' onClick={()=>navigate('/profile')}><FaUserCircle size={24}/></button>
          <button className='class1-btn' onClick={toggleHelp}><FaQuestionCircle size={20}/></button>
        </div>
      </header>
      <div className='body-section'>
        <aside className={`side-panel ${collapsed? 'collapsed':''}`} style={{margin:0}}>
          {!collapsed && <div className='sidebar-image'><img src={airplaneImage} alt='Airplane'/></div>}
          <nav className='side-menu'>
            <button onClick={()=>navigate('/home')}><FaHome/> {!collapsed && <span>Home</span>}</button>
            <button onClick={()=>openInNewTab('https://ieg-america.com/')}><FaGlobe/> {!collapsed && <span>IEG Website</span>}</button>
            <button onClick={()=>openInNewTab('https://app.loungeatlas.com/')}><FaMobileAlt/> {!collapsed && <span>Lounge Atlas App</span>}</button>
            <button onClick={handleLogout}><FaSignOutAlt/> {!collapsed && <span>Sign Out</span>}</button>
          </nav>
        </aside>
        <main className='main-panel'>
          <div className='main-card insights-card' style={{minHeight:1520, minWidth:1240, display:'flex', flexDirection:'column'}}>
            <div className='main-content' style={{flex:1, display:'flex', flexDirection:'column'}}>
              <h2 style={{marginBottom:20, fontSize:34}}>Insights</h2>
              <div style={{display:'flex', justifyContent:'flex-end', marginBottom:16}}>
                <div style={{display:'flex', gap:12, alignItems:'center'}}>
                  <span style={{fontSize:13, fontWeight:600, color:'#0e265a'}}>Category:</span>
                  <select value={category} onChange={e=>setCategory(e.target.value)} className='insights-filter-value-select'>
                    <option value='transactions'>Transactions</option>
                    <option value='subscriptions'>Subscriptions</option>
                  </select>
                </div>
              </div>
              <div className='insights-filters-wrapper'>
                <div className='insights-available-filters' onDragOver={onDragOver} onDrop={onDropAvailable}>
                  <h4>Available Filters</h4>
                  <div className='insights-filter-pool'>
                    {availableFilters.map(f=> <div key={f.key} className='insights-filter-chip' draggable onDragStart={e=>onDragStart(e,f,'available')} onClick={()=>addFilter(f)}>{f.label}</div>)}
                  </div>
                </div>
                <div className='insights-active-filters'>
                  <h4>Active Filters (Drag here)</h4>
                  <div className='insights-drop-zone' onDragOver={onDragOver} onDrop={onDropActive}>
                    {activeFilters.map(f=> (
                      <div key={f.key} className='insights-filter-chip' draggable onDragStart={e=>onDragStart(e,f,'active')}>
                        <span>{initialFilterPool.find(p=>p.key===f.key)?.label}</span>
                        {renderValueControl(f.key)}
                        <button onClick={()=>removeFilter(f.key)} aria-label='Remove filter'><FaTimes/></button>
                      </div>
                    ))}
                    {activeFilters.length===0 && <span style={{fontSize:12, color:'#4b6584'}}>Drag filters here or click from Available.</span>}
                  </div>
                </div>
              </div>
              <div className='insights-metrics-bar'>
                {(category==='transactions' && filtersState.metric.startsWith('transaction') && txData) && (
                  <div className='insights-metric-card'>
                    <h5>Total Amount</h5>
                    <div className='insights-metric-value'>{txData.total_amount.toFixed(2)}</div>
                    <div className='insights-metric-sub'>Across {txData.total_count} tx</div>
                  </div>
                )}
                {category==='transactions' && filtersState.metric==='transaction_count' && txData && (
                  <div className='insights-metric-card' style={{background:'#1572dd'}}>
                    <h5>Transactions</h5>
                    <div className='insights-metric-value'>{txData.total_count}</div>
                    <div className='insights-metric-sub'>In range</div>
                  </div>
                )}
        {category==='subscriptions' && filtersState.metric==='subscription_count' && subData && (
                  <div className='insights-metric-card' style={{background:'#1565c0'}}>
                    <h5>Subscriptions</h5>
          <div className='insights-metric-value'>{subData.total_count !== null ? subData.total_count : '—'}</div>
                    <div className='insights-metric-sub'>{filtersState.subscription_status || 'All Statuses'}</div>
                  </div>
                )}
              </div>
              <div className='insights-chart-wrapper' style={{flex:1}}>
                <div className='insights-chart-header'>
                  <div style={{display:'flex', flexDirection:'column', gap:4}}>
                    <h4 style={{margin:0}}>Visualization</h4>
                    <div className='insights-filter-summary' style={{fontSize:12, color:'#4b6584', lineHeight:1.4}}>
                      <strong>Applied:</strong> {summaryParts.length? summaryParts.join(' | '): 'None'}
                    </div>
                  </div>
                  {loading && <div className='insights-loading'>Loading…</div>}
                </div>
                {(!loading && category==='transactions' && filtersState.metric.startsWith('transaction') && txChartData.length>0) && (
                  <Chart chartType='PieChart' data={googlePieData} options={pieOptions} width='100%' height='260px'/>
                )}
                {(!loading && category==='transactions' && filtersState.metric.startsWith('transaction') && txChartData.length===0) && (
                  <div style={{padding:24, fontSize:13, color:'#4b6584'}}>No data for selected parameters.</div>
                )}
        {(!loading && category==='subscriptions' && filtersState.metric==='subscription_count' && subData) && (()=>{
                  const series = subSeriesChartData(subData);
                  if(series.length===0) return <div style={{padding:24, fontSize:13, color:'#4b6584'}}>No data for selected parameters.</div>;
                  const lineData = [['Bucket','Subscriptions'], ...series.map(s=>[s.name,s.value])];
                  return (
                    <div>
          <Chart chartType='LineChart' data={lineData} width='100%' height='260px' options={{legend:{position:'right'}, chartArea:{width:'70%', height:'70%'}, colors:['#1565c0'], pointSize:5, fontName:'Inter, Arial, sans-serif', vAxis:{ title: metricYAxisTitle, titleTextStyle:{ color:'#0e265a', fontSize:12 } }, hAxis:{ title:'Time Bucket', titleTextStyle:{ color:'#0e265a', fontSize:12 } }}} />
          <div style={{padding:'8px 24px 0', fontSize:12, color:'#0e265a'}}>Total subscriptions in range: <strong>{subData.total_count}</strong> {filtersState.subscription_status && <>| Status: <strong>{filtersState.subscription_status}</strong></>} {filtersState.plan_tier && <>| Plan Tier: <strong>{filtersState.plan_tier}</strong></>}</div>
                    </div>
                  );
                })()}
                {loading && <div style={{padding:24, fontSize:13, color:'#4b6584'}}>Loading chart…</div>}
              </div>
              {fkPopup && (
                <div className='fk-popup-overlay' style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:90}} onClick={closeFkPopup}>
                  <div className='fk-popup' onClick={e=>e.stopPropagation()} role='dialog' aria-modal='true'>
                    <div className='fk-popup-header'>
                      <h5 style={{margin:0, fontSize:16}}>{fkPopup.key==='lounge_id'?'Lounge':'User'} Details</h5>
                      <button className='fk-popup-close' onClick={closeFkPopup} aria-label='Close popup'><FaTimes/></button>
                    </div>
                    <div className='fk-popup-body'>
                      {fkPopup.loading && <div className='fk-popup-loading'>Loading details…</div>}
                      {!fkPopup.loading && fkPopup.data && !fkPopup.data.__error && (
                        <table className='fk-popup-table'><tbody>
                          {Object.entries(fkPopup.data).slice(0,40).map(([k,v])=> {
                            const raw = typeof v==='object'? JSON.stringify(v): String(v);
                            const isUrl = /^https?:\/\//i.test(raw);
                            const long = raw.length > 60;
                            const display = long ? raw.slice(0,60) + '…' : raw;
                            return (
                              <tr key={k}>
                                <th>{k}</th>
                                <td className='fk-val-cell'>
                                  <span className={`fk-val-text ${isUrl?'fk-val-url':''}`} title={raw}>
                                    {isUrl ? <a href={raw} target='_blank' rel='noopener noreferrer'>{display}</a> : display}
                                  </span>
                                  {long && (
                                    <button type='button' className='fk-copy-btn' aria-label='Copy full value' onClick={()=>handleCopy(raw)}><FaCopy size={12}/></button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody></table>
                      )}
                      {!fkPopup.loading && fkPopup.data && fkPopup.data.__error && <div className='fk-popup-empty'>{fkPopup.data.__message || 'Not found.'}</div>}
                    </div>
                  </div>
                </div>
              )}
              {showHelp && (
                <div className='fk-popup-overlay' style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:80}} onClick={toggleHelp}>
                  <div className='fk-popup' style={{maxWidth:600}} onClick={e=>e.stopPropagation()}>
                    <div className='fk-popup-header'>
                      <h5 style={{margin:0}}>Insights Help</h5>
                      <button className='fk-popup-close' onClick={toggleHelp} aria-label='Close help'><FaTimes/></button>
                    </div>
                    <div className='fk-popup-body' style={{fontSize:13, lineHeight:1.5}}>
                      <p>Drag filters from Available to Active or click to activate. Use info icons in Lounge/User dropdowns for quick detail previews.</p>
                      <p>Metric selector changes the aggregation displayed in the chart. Date preset and granularity control the temporal slice.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Insights;
